import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PublicUser, UserRole } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { CheckboxField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { UserFormDialog } from '../components/user-form-dialog.js'
import { PasswordResetDialog } from '../components/password-reset-dialog.js'
import { format, t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { listUsers, updateUser } from '../lib/users.js'

type DialogState =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; user: PublicUser }
  | { kind: 'reset'; user: PublicUser }

export function UsersPage() {
  const strings = t()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' })
  const [notice, setNotice] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['users', { search, includeInactive }],
    queryFn: () => listUsers({ search: search || undefined, includeInactive }),
  })

  const toggleActive = useMutation({
    mutationFn: (user: PublicUser) => updateUser(user.id, { isActive: !user.isActive }),
    onSuccess: async (updated) => {
      setNotice(
        updated.isActive ? strings.users.activate.success : strings.users.deactivate.success,
      )
      await queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const roleLabel = (role: UserRole) => strings.users.roles[role]

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{strings.users.heading}</h1>
          <p className="text-text-muted mt-1 text-sm">{strings.users.intro}</p>
        </div>
        <Button onClick={() => setDialog({ kind: 'create' })}>
          {strings.users.actions.create}
        </Button>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}

      {toggleActive.isError && (
        <Alert tone="danger">
          {toggleActive.error instanceof ApiError
            ? (toggleActive.error.problem.detail ?? strings.errors.unexpected)
            : strings.errors.unexpected}
        </Alert>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-64">
          <TextField
            label={strings.users.searchLabel}
            placeholder={strings.users.searchPlaceholder}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="pb-2.5">
          <CheckboxField
            label={strings.users.showInactive}
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
        </div>
      </div>

      {query.isError && <Alert tone="danger">{strings.users.loadError}</Alert>}

      {query.isPending && <p className="text-text-muted text-sm">{strings.app.loading}</p>}

      {query.data && (
        <>
          <p className="text-text-muted text-sm">
            {query.data.total === 1
              ? strings.users.countOne
              : format(strings.users.countMany, { count: query.data.total })}
          </p>

          {query.data.users.length === 0 ? (
            <p className="text-text-muted text-sm">{strings.users.empty}</p>
          ) : (
            <div className="border-border overflow-x-auto rounded-lg border">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-surface-sunken text-text-muted">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      {strings.users.columns.name}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      {strings.users.columns.email}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      {strings.users.columns.role}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      {strings.users.columns.status}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      {strings.users.columns.lastLogin}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right font-medium">
                      {strings.users.columns.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.users.map((user) => (
                    <tr key={user.id} className="border-border border-t">
                      <td className="px-4 py-2.5 font-medium">{user.name}</td>
                      <td className="text-text-muted px-4 py-2.5">{user.email}</td>
                      <td className="px-4 py-2.5">
                        <Badge tone={user.role === 'admin' ? 'info' : 'success'}>
                          {roleLabel(user.role)}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        {user.isActive ? (
                          <Badge tone="success">{strings.users.status.active}</Badge>
                        ) : (
                          <Badge tone="warning">{strings.users.status.inactive}</Badge>
                        )}
                      </td>
                      <td className="text-text-muted px-4 py-2.5">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleString('de-DE')
                          : strings.users.neverLoggedIn}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDialog({ kind: 'edit', user })}
                          >
                            {strings.users.actions.edit}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDialog({ kind: 'reset', user })}
                          >
                            {strings.users.actions.resetPassword}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            // Deactivating yourself would lock you out of the
                            // very screen that could undo it.
                            disabled={user.id === currentUser?.id || toggleActive.isPending}
                            onClick={() => {
                              setNotice(null)
                              toggleActive.mutate(user)
                            }}
                          >
                            {user.isActive
                              ? strings.users.actions.deactivate
                              : strings.users.actions.activate}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <UserFormDialog
        open={dialog.kind === 'create' || dialog.kind === 'edit'}
        user={dialog.kind === 'edit' ? dialog.user : null}
        onClose={() => setDialog({ kind: 'none' })}
        onSaved={(message) => {
          setNotice(message)
          setDialog({ kind: 'none' })
        }}
      />

      <PasswordResetDialog
        open={dialog.kind === 'reset'}
        user={dialog.kind === 'reset' ? dialog.user : null}
        onClose={() => setDialog({ kind: 'none' })}
      />
    </div>
  )
}
